import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { buildLinkJournalPatchBody, buildUnlinkJournalPatchBody } from '../../lib/linkJournalToSale';

/**
 * "Link to sale" box shared by the journal view drawer (viewJournal.js,
 * startExpanded) and the edit-journal drawer (newJournal.js, collapsed pill).
 *
 * Linking ADDS the journal to the chosen sale — it never detaches the journal
 * from sales it is already linked to. One journal may be linked to several
 * sales (confirmed design). Sales already linked show as blue primary
 * buttons; clicking one asks to REMOVE that single link (unlink toggles
 * only the clicked sale, never the others).
 *
 * Props:
 * - stores: the store bag (uses saleStore + userStore).
 * - journal: the journal object to embed in the target sale.
 * - animal: the journal's animal; animal.owner drives the recent-sales list
 *   and the animal is added to the target sale when missing.
 * - onLinked(targetSale, action): called after a successful PATCH so the host
 *   can refresh itself; action is 'link' or 'unlink'. May be async.
 * - startExpanded: true = box always visible and recent sales loaded as soon
 *   as the owner is known (viewJournal behavior). false = only a small
 *   "Link to sale" pill; expanding lazily loads the recent sales.
 */
@observer
class LinkJournalToSale extends Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: !!props.startExpanded,
            visitorSales: [],
            linkSaleNumber: '',
            linkLoading: false,
            linkError: null,
            linkSuccess: false,
            showAllSales: false,
            confirmLinkSaleId: null,
            confirmAction: 'link',
            lastAction: 'link',
        };
        // Owner id the recent-sales list was last loaded for. Instance field
        // (not state) so the guard in maybeLoadVisitorSales is synchronous and
        // componentDidUpdate re-entries can't trigger duplicate loads.
        this.loadedForOwner = null;
    }

    maybeLoadVisitorSales = () => {
        const { animal } = this.props;
        const owner = animal?.owner;
        if (!this.state.expanded || !owner) {
            return;
        }
        if (this.loadedForOwner === owner) {
            return;
        }
        this.loadedForOwner = owner;
        this.loadVisitorSales(owner);
    }

    loadVisitorSales = async owner => {
        const { saleStore } = this.props.stores;
        const response = await saleStore.load({
            query: {
                visitor: [owner],
            },
            skipUpdate: true,
            limit: 20,
            sort: '-saleDate',
        });
        this.setState({ visitorSales: response || [] });
    }

    onClickExpand = e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ expanded: true });
    }

    onClickLinkToSale = e => {
        e.preventDefault();
        e.stopPropagation();
        const buttonEl = e.target.closest('button');
        const saleId = parseInt(buttonEl?.dataset?.saleId || this.state.linkSaleNumber, 10);
        if (!saleId || isNaN(saleId)) {
            return;
        }
        // Clicking an already-linked (blue) sale toggles: it asks to remove
        // that single link instead of re-linking.
        const confirmAction = buttonEl?.dataset?.linked === '1' ? 'unlink' : 'link';
        this.setState({ confirmLinkSaleId: saleId, confirmAction, linkError: null, linkSuccess: false });
    }

    onConfirmLinkToSale = async () => {
        const saleId = this.state.confirmLinkSaleId;
        const action = this.state.confirmAction;
        if (!saleId) {
            return;
        }
        this.setState({ linkLoading: true, linkError: null, linkSuccess: false, confirmLinkSaleId: null });

        const { saleStore } = this.props.stores;
        const { journal, animal, onLinked } = this.props;

        try {
            // Load target sale
            const saleData = await saleStore.load(saleId, false, { skipUpdate: true });
            if (!saleData || !saleData[0]) {
                this.setState({ linkLoading: false, linkError: 'Sale not found' });
                return;
            }
            const targetSale = saleData[0];
            const patchBody = action === 'unlink'
                ? buildUnlinkJournalPatchBody({ targetSale, journal })
                : buildLinkJournalPatchBody({ targetSale, journal, animal });

            // PATCH the sale with updated journals (and animals if needed)
            await util.fetchApi(`/api/sales/${saleId}`, { method: 'PATCH' }, patchBody);

            this.setState({ linkLoading: false, linkSuccess: true, lastAction: action, linkSaleNumber: '' });

            // Refresh the recent-sales list so the toggled sale shows its new
            // linked/unlinked state with up-to-date embedded animals.
            this.loadedForOwner = null;
            this.maybeLoadVisitorSales();

            if (onLinked) {
                try {
                    await onLinked(targetSale, action);
                } catch (err) {
                    // The link/unlink itself succeeded — a failed host refresh
                    // must not be reported as a failed action.
                    console.error('linkJournalToSale: onLinked callback failed after successful link', err);
                }
            }
        } catch (err) {
            this.setState({ linkLoading: false, linkError: err.message || 'Failed to link journal' });
        }
    }

    onCancelLinkToSale = () => {
        this.setState({ confirmLinkSaleId: null });
    }

    onChangeLinkSaleNumber = e => {
        this.setState({ linkSaleNumber: e.target.value });
    }

    componentDidMount() {
        this.maybeLoadVisitorSales();
    }

    componentDidUpdate() {
        this.maybeLoadVisitorSales();
    }

    render() {
        const { journal = {} } = this.props;
        const {
            expanded,
            visitorSales = [],
            linkSaleNumber,
            linkLoading,
            linkError,
            linkSuccess,
            showAllSales,
            confirmLinkSaleId,
            confirmAction,
            lastAction,
        } = this.state;

        if (!(journal.id > 0)) {
            return null;
        }

        const { userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (!expanded) {
            return (
                <div class='d-flex flex-row mx-4 mt-3'>
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-secondary rounded-pill'
                        onClick={this.onClickExpand}
                    >
                        <i class='fa-duotone fa-link me-1' />
                        <Text id='dyrejournal.journal.link-to-sale'>Link to sale</Text>
                    </button>
                </div>
            );
        }

        return (<>
            <div class='font-weight-lighter text-muted mx-4 mt-3'>
                <small class='text-uppercase ms-3'>
                    <Text id='dyrejournal.journal.link-to-sale'>Link to sale</Text>
                </small>
            </div>
            <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex flex-row align-items-center mb-2'>
                    <input
                        type='number'
                        class='form-control form-control-sm me-2'
                        placeholder='Sale number'
                        value={linkSaleNumber}
                        onInput={this.onChangeLinkSaleNumber}
                        style='max-width: 200px;'
                    />
                    <button
                        type='button'
                        class='btn btn-sm btn-primary rounded-pill'
                        disabled={linkLoading || !linkSaleNumber}
                        onClick={this.onClickLinkToSale}
                    >
                        {linkLoading ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-link' />}
                        {' '}<Text id='dyrejournal.link'>Link</Text>
                    </button>
                </div>

                {confirmLinkSaleId > 0 && (
                    <div class='alert alert-warning d-flex flex-column py-2 px-3 mb-2'>
                        <small class='mb-2'>
                            {confirmAction === 'unlink' ? (
                                <Text id='dyrejournal.confirm-unlink' fields={{ journal: { id: journal.id }, confirmLinkSaleId }}>Remove the link between journal {journal.id} and sale #{confirmLinkSaleId}?</Text>
                            ) : (
                                <Text id='dyrejournal.confirm-link' fields={{ journal: { id: journal.id }, confirmLinkSaleId }}>Link journal {journal.id} to sale #{confirmLinkSaleId}?</Text>
                            )}
                        </small>
                        <div class='d-flex flex-row'>
                            <button
                                type='button'
                                class={`btn btn-sm ${confirmAction === 'unlink' ? 'btn-danger' : 'btn-warning'} rounded-pill me-2`}
                                disabled={linkLoading}
                                onClick={this.onConfirmLinkToSale}
                            >
                                {linkLoading ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class={`fa-duotone ${confirmAction === 'unlink' ? 'fa-link-slash' : 'fa-check'}`} />}
                                {' '}<Text id='dyrejournal.confirm'>Confirm</Text>
                            </button>
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                disabled={linkLoading}
                                onClick={this.onCancelLinkToSale}
                            >
                                <Text id='dyrejournal.cancel'>Cancel</Text>
                            </button>
                        </div>
                    </div>
                )}

                {linkError && <div class='text-danger mb-2'><small>{linkError}</small></div>}
                {linkSuccess && <div class='text-success mb-2'><small><i class='fa-duotone fa-check' /> {lastAction === 'unlink'
                    ? <Text id='dyrejournal.unlinked'>Link removed!</Text>
                    : <Text id='dyrejournal.linked'>Linked!</Text>}</small></div>}

                {visitorSales.length > 0 && <>
                    <small class='text-body-secondary mb-1'>
                        <Text id='dyrejournal.recent-sales'>Recent sales for this owner</Text>:
                    </small>
                    {(showAllSales ? visitorSales : visitorSales.slice(0, 3)).map(sale => {
                        const isLinked = sale.journals?.some(j => j.id === journal.id);
                        return (
                            <button
                                key={sale.id}
                                type='button'
                                class={`btn btn-sm ${isLinked ? 'btn-primary' : 'btn-outline-secondary'} rounded-pill mb-1 text-start`}
                                data-sale-id={sale.id}
                                data-linked={isLinked ? '1' : ''}
                                disabled={linkLoading}
                                onClick={this.onClickLinkToSale}
                            >
                                {isLinked && <><i class='fa-duotone fa-link' />{' '}</>}
                                #{sale.saleNumber || sale.id}
                                {sale.saleDate && <> - {util.isoDate(sale.saleDate, false, false, true)}</>}
                                {sale.animals?.length > 0 && <> - {sale.animals.map(a => a.name).join(', ')}</>}
                            </button>
                        );
                    })}
                    {!showAllSales && visitorSales.length > 3 && (
                        <button
                            type='button'
                            class='btn btn-sm btn-link text-start ps-0'
                            onClick={() => this.setState({ showAllSales: true })}
                        >
                            <Text id='dyrejournal.show-more'>Show more</Text> ({visitorSales.length - 3})
                        </button>
                    )}
                </>}
            </div>
        </>);
    }
}

export default LinkJournalToSale;
